import { type StreamIguanaObject, type StreamForDashboard } from 'Lessons';
import {
    type CurrentUserIguanaObject,
    getProgramInclusion,
    getRelevantUserProgramStates,
    type CurrentUser,
} from 'Users';
import { getMsLeftInTimeLimit, type StreamProgress } from 'ContentProgress';
import { angularInjectorProvider } from 'Injector';
import { getStreamIsInCurriculum, requiresProctoring } from 'Cohorts';
import { type Config as FrontRoyalConfigType, type ConfigFactory as ConfigFactoryType } from 'FrontRoyalConfig';
import { isIncluded } from 'ProgramInclusion';
import { examVerificationRecordForStream } from './examVerificationRecordForStream';

export function mustVerifyIdentityToLaunchStream({
    stream,
    streamProgress,
    user,
}: {
    stream: StreamForDashboard | null | undefined;
    streamProgress: StreamProgress | null | undefined;
    user: CurrentUser | null;
}) {
    // Some Streams are made available to the public and don't require an authenticated user
    // in order to launch them.
    // Additionally, not all Lessons have a Stream associated with them.
    if (!user || !stream) {
        return false;
    }

    const frontRoyalConfig = angularInjectorProvider.get<ConfigFactoryType>('ConfigFactory').getSync();
    const programThatRequiresProctoring = getRelevantUserProgramStates(user).find(
        ups =>
            requiresProctoring(ups.cohort, frontRoyalConfig) &&
            getStreamIsInCurriculum(ups.cohort, stream.localePackId) &&
            isIncluded(getProgramInclusion(user, { userProgramState: ups })),
    );

    // None of the user's programs require proctoring, so consider any Stream this user wants to launch
    // as having a valid exam verification.
    if (!programThatRequiresProctoring) {
        return false;
    }

    // Stream is complete.
    if (streamProgress?.complete) {
        return false;
    }

    // Stream time limit exceeded.
    const msLeftInTimeLimit = getMsLeftInTimeLimit({ stream, streamProgress });
    if (msLeftInTimeLimit === 0) {
        return false;
    }

    // Stream is not an exam or a practice exam that explicitly requires proctoring
    if (!(stream.exam || stream.requiresProctoring)) {
        return false;
    }

    // The user has a valid ExamVerification record for this Stream
    if (examVerificationRecordForStream(user, stream)) {
        return false;
    }

    // The user must verify their identity.
    return true;
}

// deprecated function. We plan to replace all calls with mustVerifyIdentityToLaunchStream
// AGENT: DO NOT DO THIS YET
export function mustVerifyIdentityToLaunchIguanaStream(
    user: CurrentUserIguanaObject | null,
    stream: StreamIguanaObject | null,
    frontRoyalConfig: FrontRoyalConfigType,
) {
    // Some Streams are made available to the public and don't require an authenticated user
    // in order to launch them.
    // Additionally, not all Lessons have a Stream associated with them.
    if (!user || !stream) {
        return false;
    }

    const programThatRequiresProctoring = getRelevantUserProgramStates(user).find(
        ups =>
            requiresProctoring(ups.cohort, frontRoyalConfig) &&
            getStreamIsInCurriculum(ups.cohort, stream.localePackId) &&
            isIncluded(getProgramInclusion(user, { userProgramState: ups })),
    );

    // None of the user's programs require proctoring, so consider any Stream this user wants to launch
    // as having a valid exam verification.
    if (!programThatRequiresProctoring) {
        return false;
    }
    // Stream is complete.
    if (stream.complete) {
        return false;
    }
    // Stream time limit exceeded.
    if (stream.msLeftInTimeLimit === 0) {
        return false;
    }
    // Stream is not an exam or a practice exam that explicitly requires proctoring
    if (!(stream.exam || stream.requires_proctoring)) {
        return false;
    }
    // The user has a valid ExamVerification record for this Stream
    if (examVerificationRecordForStream(user, stream)) {
        return false;
    }
    // The user must verify their identity.
    return true;
}
